import { Box } from '@material-ui/core';
import { useMemo, useState } from 'react';

import { AIIcon } from '@setvi/shared/components/sicons';
import { Product } from '@setvi/shared/interfaces';

import { useSalesKitStore } from 'componentsV2/product/details/store';

import { ProductSpecifications } from '../specifications';

export enum ProductDetailsTab {
  DETAILS = 1,
  AIDETAILS = 2
}

export enum ProductDetailsAccordionTitles {
  Description = 'Description',
  Specifications = 'Specifications'
}

export interface ProductDetailsAccordion {
  title: string;
  content: (product: Product) => JSX.Element;
}

export const productDetailsAccordions = [
  {
    title: ProductDetailsAccordionTitles.Description,
    content: (product: Product) => (
      <Box dangerouslySetInnerHTML={{ __html: product.description || '-' }} />
    )
  },
  {
    title: ProductDetailsAccordionTitles.Specifications,
    content: (product: Product) => <ProductSpecifications product={product} />
  }
];

export const useProductInfo = ({ product }: { product: Product }) => {
  const [tab, setTab] = useState(ProductDetailsTab.DETAILS);
  const [selectedImage, setSelectedImage] = useState('');

  const [expandedAccordion, setExpandedAccordion] = useState<
    ProductDetailsAccordion['title'][]
  >(productDetailsAccordions.map(({ title }) => title));

  const { salesKit, salesKitLoading } = useSalesKitStore();

  const salesKitData = useMemo(() => {
    if (salesKit?.disabled || !salesKit?.data?.length || !salesKit) {
      setTab(ProductDetailsTab.DETAILS);
      return null;
    }

    return salesKit?.data;
  }, [salesKit]);

  const options = useMemo(
    () => [
      { label: 'Product Details', value: ProductDetailsTab.DETAILS },
      {
        label: 'AI Overview',
        value: ProductDetailsTab.AIDETAILS,
        icon: (
          <AIIcon
            fontSize="small"
            fill={tab !== ProductDetailsTab.AIDETAILS ? '#696974' : ''}
          />
        )
      }
    ],
    [tab]
  );

  const imagePathsKey = product?.images?.map(i => i?.path).join(',') ?? '';

  const images = useMemo(
    () =>
      product?.images?.length > 0
        ? product.images
            .map(image => image?.path)
            // Removing duplicates because in some cases the same image url is added multiple
            // times which causes issues when navigating through the gallery
            .filter((v, i, a) => a?.indexOf(v) === i)
        : ['/images/product-placeholder.svg'],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imagePathsKey]
  );

  return {
    tab,
    setTab,
    salesKit,
    expandedAccordion,
    salesKitLoading,
    setExpandedAccordion,
    selectedImage,
    setSelectedImage,
    salesKitData,
    options,
    images
  };
};
